/**
 * CALL-CENTER-LEAD-360-SALESFORCE-EXPERIENCE-V1 -- the horizontal lifecycle
 * STATUS PATH BAR (Mission 1; the Salesforce-path grammar rendered in
 * AdoraCare's own token language -- benchmark, never a clone).
 *
 * Renders THE existing stage model (buildStageRailModel over the engine
 * mirror, lead-lifecycle-rail-view.ts) as a compact horizontal path:
 * completed stages check off, the committed current stage highlights, the
 * disposition-derived `contacted` node stays a DERIVED marker (attempt
 * count in the hover title), and `lost` renders as a distinct terminal
 * chip after the forward path -- exactly the vertical rail's honesty,
 * re-shaped for the record header area.
 *
 * LEAD-360-CONFORMANCE-B1: restyled to the approved pack's "slim dated
 * line" grammar (docs/design-studio/lead-360/APPROVED-DIRECTION-SYSTEM-EXTRACT.md
 * TIER 2 "LIFECYCLE LINE AS CONTEXT") -- reached = filled dot, derived =
 * dashed dot, current = ringed brand dot + bold label, future = ghost dot.
 * PIXELS ONLY: the data (node.state/active/derived/attemptCount) is
 * unchanged; this component still carries NO per-stage entry DATE (the
 * engine mirror -- lead-lifecycle-rail-view.ts StageRailNode -- has no date
 * field to render honestly), so the pack's "+ date" per node is a named
 * CONFORMANCE GAP (see the run record), not fabricated here.
 *
 * READ-ONLY BY CONSTRUCTION: no button, no click handler, no stage writer
 * -- stage advancement stays system-driven / authority-gated server-side
 * (api/routes/lead-stage.ts); this bar can never advance a stage. The
 * honest empty state renders when the wire carried no canon stage.
 */

import type { Lead360Input } from '@/dashboard/owner/lead-360-view';
import { buildStageRailModel } from '@/dashboard/leads/lead-lifecycle-rail-view';

export const STAGE_PATH_NOT_RECORDED_COPY = 'Lifecycle stage not recorded yet.';
export const STAGE_PATH_LOST_HINT =
  'Lost is terminal - the pre-lost stage is not recoverable from the record.';

export default function LeadStagePathBar({
  record,
}: {
  record: Lead360Input;
}): JSX.Element {
  const model = buildStageRailModel(record.stage, record.events);
  return (
    <nav
      className="lead-stagepath"
      aria-label="Lifecycle stage path"
      data-testid="lead-stagepath"
      data-current-stage={model.currentStage ?? ''}
    >
      <ol className="lead-stagepath__track">
        {model.ladder.map((node) => (
          <li
            key={node.stage}
            className="lead-stagepath__step"
            data-stage={node.stage}
            data-state={node.state}
            data-active={node.active ? 'yes' : 'no'}
            aria-current={node.active ? 'step' : undefined}
            title={
              node.derived
                ? node.attemptCount !== null && node.attemptCount > 0
                  ? `Derived from logged calls - ${node.attemptCount} contact ${
                      node.attemptCount === 1 ? 'attempt' : 'attempts'
                    } logged`
                  : // HONEST DERIVED COPY (LEAD-TRUTH-AND-DEPENDENT-STATE-
                    // CORRECTIVE-V1): this marker COUNTS logged attempts; the
                    // recorded stage itself never stops here (contacted is
                    // disposition-derived, not a committed rung) -- the old
                    // "advances automatically" wording promised a stage write
                    // no machinery performs.
                    'Derived marker - counts logged contact attempts; the recorded stage advances at qualification or booking'
                : undefined
            }
          >
            <span
              className="lead-stagepath__dot"
              aria-hidden="true"
              data-testid="lead-stagepath-dot"
            />
            <span className="lead-stagepath__label">
              {node.state === 'done' ? (
                <span className="lead-stagepath__check" aria-hidden="true">
                  {'\u2713 '}
                </span>
              ) : null}
              {node.label}
            </span>
          </li>
        ))}
        <li
          className="lead-stagepath__step lead-stagepath__step--terminal"
          data-stage="lost"
          data-state={model.terminalNode.state}
          data-active={model.terminalNode.active ? 'yes' : 'no'}
          aria-current={model.terminalNode.active ? 'step' : undefined}
          title={model.isLost ? STAGE_PATH_LOST_HINT : 'Terminal disposition - not reached'}
        >
          <span className="lead-stagepath__dot" aria-hidden="true" />
          <span className="lead-stagepath__label">{model.terminalNode.label}</span>
        </li>
      </ol>
      {!model.known ? (
        <p className="lead-stagepath__empty lead360-muted" data-testid="lead-stagepath-empty">
          {STAGE_PATH_NOT_RECORDED_COPY}
        </p>
      ) : null}
    </nav>
  );
}
